import DOMPurify from 'dompurify';

import type { EditorStarterAsset } from '../../assets/assets';

/**
 * sanitize 전 원본 마크업을 꺼낸다 — 업로드로 보존된 마크업이 우선, 없으면 data URI 를 디코드.
 * 둘 다 없으면(=일반 업로드 SVG) null 을 돌려 <Img> 폴백으로 보낸다.
 */
const readRawSvgMarkup = (asset: EditorStarterAsset): string | null => {
  const markup = asset.inlineSvgMarkup;
  if (typeof markup === 'string' && markup.length > 0) return markup;

  const url = asset.remoteUrl;
  if (!url || !url.startsWith('data:')) return null;
  const commaIndex = url.indexOf(',');
  if (commaIndex === -1) return null;
  // decorations.ts 가 encodeURIComponent 로 인코딩하므로 역으로 디코드해 원본 마크업 복원.
  return decodeURIComponent(url.slice(commaIndex + 1));
};

/**
 * 신뢰된 인라인 SVG 마크업을 추출한다 (dangerouslySetInnerHTML XSS 게이트).
 *
 * 반환값이 non-null 일 때만 호출부(ImageLayer)가 인라인 <svg> 로 렌더한다.
 * null 이면 일반 <Img> 경로(이미지로 로드된 SVG 는 스크립트 미실행)로 폴백한다.
 *
 * 게이트 순서:
 *  1. asset.trustedInlineSvg === true — 우리 코드(decorations.ts 등)가 생성했다는 출처 마커.
 *     업로드/BE content 매핑 파이프라인은 이 플래그를 세팅하지 않으므로 신뢰 불가 에셋은 여기서 막힌다.
 *     (mimeType/scheme 는 신뢰 불가 출처가 채울 수 있는 메타데이터라 단독 가드로는 부족하다.)
 *  2. mimeType === 'image/svg+xml' && (inlineSvgMarkup 보유 || remoteUrl 이 data: URI) — 우리가
 *     만든 SVG 만 읽는다. inlineSvgMarkup 은 업로드로 remoteUrl 이 publicUrl 로 치환된 뒤에도
 *     인라인 렌더를 유지하기 위한 통로다(assets.ts 필드 주석 참조) — 미리보기와 에디터가 같은
 *     경로로 그려져야 결과가 갈리지 않는다.
 *  3. DOMPurify(SVG 프로파일) sanitize — 해소됨. admin raw JSON 편집으로 deco[].svg 가
 *     trustedInlineSvg 마커를 단 채 BE 에 영속 저장되는 경로가 열려(단위 3), "우리 코드가 만든
 *     문자열"이라는 전제가 더 이상 성립하지 않는다. 마커를 신뢰 소스가 아니라 "인라인 렌더 허용"
 *     게이트로 재해석하고, 실제 무해성은 이 sanitize 가 보장한다.
 *
 * 호출 컨텍스트: 이 함수는 항상 브라우저 DOM(에디터/프리뷰 클라이언트 컴포넌트 마운트 후,
 * 또는 람다 렌더용 헤드리스 Chrome) 안에서만 실행된다 — Next.js SSR 경로에서는 호출되지 않는다
 * (DeckFlowPreview 는 bake 결과가 client effect 로 채워지기 전까지 Player/ImageLayer 를 렌더하지
 * 않는다). `DOMPurify()` 를 호출 시점에 실행해 그 순간의 전역 `window` 를 바인딩한다(모듈
 * import 시점에 미리 만든 싱글턴을 쓰지 않음) — 브라우저에서는 항상 정상 동작하고, jsdom 기반
 * 테스트에서도 (window 를 먼저 세팅해두면) 같은 코드 경로로 검증할 수 있다.
 */
export function getTrustedInlineSvgMarkup(
  asset: EditorStarterAsset | undefined,
): string | null {
  if (asset?.trustedInlineSvg !== true) return null;
  if (asset.mimeType !== 'image/svg+xml') return null;

  try {
    const raw = readRawSvgMarkup(asset);
    if (raw === null) return null;
    const purifier = DOMPurify();
    // window 없는 컨텍스트(예: 향후 SSR 경로로 잘못 끌려온 caller)에선 dompurify 가 에러 없이
    // dirty 원본을 그대로 반환한다(fail-open) — sanitize 가 무력화된 채 dangerouslySetInnerHTML 로
    // 흘러가는 걸 막기 위해 여기서 명시적으로 fail-closed(null → <Img> 폴백) 처리한다.
    if (!purifier.isSupported) return null;
    return purifier.sanitize(raw, {
      USE_PROFILES: { svg: true, svgFilters: true },
      FORBID_TAGS: ['script', 'foreignObject'],
      // dompurify 기본 SVG 허용 목록에 vector-effect 가 없어 제거된다 — chart 꺾은선이
      // non-scaling-stroke 로 두께를 고정하므로(chart-svg.ts buildLineSvg) 지워지면
      // preserveAspectRatio="none" 스트레치에 stroke 가 비균등 확대된다. 표현 속성이라
      // 스크립트 실행과 무관 — XSS 게이트 완화 아님.
      ADD_ATTR: ['vector-effect'],
    });
  } catch {
    return null;
  }
}
